import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { Card } from '../../components/ui';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';

interface OverallAnalytics {
  totalAttendees: number;
  totalCheckIns: number;
  totalSessions: number;
  averageSessionAttendance: number;
  popularSessions: {
    sessionId: string;
    sessionTitle: string;
    checkInCount: number;
  }[];
  checkInsByHour: { hour: number; count: number }[];
  peakArrivalPeriod: number | null;
  roomUtilization: {
    roomId: string;
    roomName: string;
    building: string;
    capacity: number;
    utilizationPercent: number;
  }[];
}

export default function AdminDashboard() {
  const { data: analytics, isLoading, error, refetch } = useQuery<OverallAnalytics>({
    queryKey: ['analytics'],
    queryFn: () => fetchApi<OverallAnalytics>('/admin/analytics'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" message="Loading analytics..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load analytics'}
        onRetry={() => refetch()}
      />
    );
  }

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-usc-garnet">Dashboard Overview</h2>
        <p className="text-usc-gray-600 mt-2">
          Real-time conference statistics and insights
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Total Attendees</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.totalAttendees || 0}
              </p>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Total Check-ins</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.totalCheckIns || 0}
              </p>
            </div>
            <div className="text-4xl">✓</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Total Sessions</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.totalSessions || 0}
              </p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Avg Attendance</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.averageSessionAttendance?.toFixed(1) || '0.0'}
              </p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </Card>
      </div>

      {/* Popular Sessions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-usc-garnet mb-4">Popular Sessions</h3>
          {analytics?.popularSessions && analytics.popularSessions.length > 0 ? (
            <div className="space-y-3">
              {analytics.popularSessions.slice(0, 5).map((session, index) => (
                <div
                  key={session.sessionId}
                  className="flex items-center justify-between p-3 bg-usc-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className="flex-shrink-0 w-6 h-6 bg-usc-garnet text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-usc-gray-900 truncate">
                      {session.sessionTitle}
                    </span>
                  </div>
                  <span className="ml-2 px-3 py-1 bg-usc-garnet text-white rounded-full text-sm font-medium">
                    {session.checkInCount}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-usc-gray-600 text-center py-8">No session data available</p>
          )}
        </Card>

        {/* Peak Arrival Period */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-usc-garnet mb-4">Check-in Activity</h3>
          {analytics?.peakArrivalPeriod !== null && analytics?.peakArrivalPeriod !== undefined ? (
            <div className="space-y-4">
              <div className="bg-usc-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-usc-gray-600 mb-2">Peak Arrival Time</p>
                <p className="text-2xl font-bold text-usc-garnet">
                  {formatHour(analytics.peakArrivalPeriod)}
                </p>
              </div>
              
              {analytics.checkInsByHour && analytics.checkInsByHour.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-usc-gray-600">Check-ins by Hour</p>
                  {analytics.checkInsByHour.slice(0, 5).map((item) => (
                    <div key={item.hour} className="flex items-center justify-between">
                      <span className="text-sm text-usc-gray-700">{formatHour(item.hour)}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-usc-gray-200 rounded-full h-2">
                          <div
                            className="bg-usc-garnet h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                (item.count / Math.max(...analytics.checkInsByHour.map((h) => h.count))) * 100
                              )}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-usc-gray-900 w-8 text-right">
                          {item.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-usc-gray-600 text-center py-8">No check-in data available</p>
          )}
        </Card>
      </div>

      {/* Room Utilization */}
      <Card className="p-6">
        <h3 className="text-xl font-bold text-usc-garnet mb-4">Room Utilization</h3>
        {analytics?.roomUtilization && analytics.roomUtilization.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.roomUtilization.map((room) => (
              <div key={room.roomId} className="bg-usc-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-usc-gray-900 truncate">{room.roomName}</p>
                    <p className="text-sm text-usc-gray-600">{room.building}</p>
                  </div>
                  <span className="ml-2 text-sm font-medium text-usc-garnet">
                    {room.utilizationPercent.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-usc-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      room.utilizationPercent >= 90
                        ? 'bg-red-500'
                        : room.utilizationPercent >= 70
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, room.utilizationPercent)}%` }}
                  />
                </div>
                <p className="text-xs text-usc-gray-600 mt-1">Capacity: {room.capacity}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-usc-gray-600 text-center py-8">No room data available</p>
        )}
      </Card>
    </div>
  );
}
